/**
 * Cache-first učitavanje za Moj dan i Obilaske (Dexie).
 */

import api from '../services/api';
import {
  CACHE_KEYS,
  MYDAY_CACHE_PREFIX,
  clearAllCache,
  deleteCache,
  getCache,
  getCacheRow,
  listCacheRows,
  setCache,
} from '../db/offlineDb';
import {
  clearAllMyDaySnapshots,
  isMyDaySnapshotValid,
  loadMyDaySnapshot,
  purgeLegacyAndExpiredMyDaySnapshots,
  saveMyDaySnapshot,
  type MyDaySnapshot,
} from './myDayCache';
import { isSnapshotCurrent, ScopeChangedError, type ScopeSnapshot } from './sessionScope';

export type VisitsPageData = {
  visits: unknown[];
  locations: unknown[];
  visitReasons: unknown[];
  errors?: Partial<Record<'visits' | 'locations' | 'visitReasons', string>>;
};

/**
 * Čita „Moj dan” samo za scope sesije koja je pokrenula čitanje (SEC-01).
 * Zapis drugog korisnika, firme, verzije prava ili stariji od roka nikad se ne vraća.
 */
export async function loadMyDayCacheFirst(dateYmd: string, session: ScopeSnapshot): Promise<{
  snapshot: MyDaySnapshot | null;
  fromCache: boolean;
}> {
  const scope = session.scope;
  if (!scope || !isSnapshotCurrent(session)) return { snapshot: null, fromCache: false };
  let found: MyDaySnapshot | null = null;
  try {
    const row = await getCacheRow(CACHE_KEYS.myDay(scope, dateYmd));
    if (row && row.scope === scope && isMyDaySnapshotValid(row.data, scope)) found = row.data;
  } catch { /* IndexedDB nedostupan */ }
  if (!found) found = loadMyDaySnapshot(scope, dateYmd);
  if (!isSnapshotCurrent(session)) return { snapshot: null, fromCache: false };
  return found ? { snapshot: found, fromCache: true } : { snapshot: null, fromCache: false };
}

/**
 * Dohvaća i sprema „Moj dan”. Odgovor koji stigne nakon promjene sesije
 * (odjava, prijava druge osobe) se odbacuje i ne sprema (ScopeChangedError).
 */
export async function fetchAndCacheMyDay(dateYmd: string, session: ScopeSnapshot): Promise<MyDaySnapshot> {
  const scope = session.scope;
  if (!scope || !isSnapshotCurrent(session)) throw new ScopeChangedError();
  const [routeRes, visitsRes] = await Promise.all([
    api.get(`/routes/date/${dateYmd}`),
    api.get('/visits', { params: { limit: 80 } }),
  ]);
  if (!isSnapshotCurrent(session)) throw new ScopeChangedError();

  const route = routeRes.data.route || null;
  const visits = visitsRes.data.visits || [];
  const active = visits.find((v: { status: string }) => v.status === 'in_progress');
  const completedToday = visits.filter((v: { status: string; started_at?: string }) => {
    if (v.status !== 'completed' || !v.started_at) return false;
    return v.started_at.slice(0, 10) === dateYmd;
  }).length;

  const snap: MyDaySnapshot = {
    scope,
    route,
    activeVisit: active || null,
    completedToday,
    savedAt: new Date().toISOString(),
  };

  const key = CACHE_KEYS.myDay(scope, dateYmd);
  try {
    await setCache(key, snap, scope);
  } catch { /* zapis u predmemoriju ne smije srušiti uspješan odgovor */ }
  if (!isSnapshotCurrent(session)) {
    // Odjava je mogla očistiti predmemoriju dok je zapis trajao: ukloni zakašnjeli zapis.
    try { await deleteCache(key); } catch { /* nedostupno */ }
    throw new ScopeChangedError();
  }
  saveMyDaySnapshot(dateYmd, snap);
  return snap;
}

/** Pokretanje: ukloni stare „Moj dan” zapise bez vlasnika i one starije od roka. */
export async function purgeLegacyMyDayCache(now = Date.now()): Promise<void> {
  purgeLegacyAndExpiredMyDaySnapshots(now);
  try {
    const rows = await listCacheRows(MYDAY_CACHE_PREFIX);
    for (const row of rows) {
      const ok = Boolean(row.scope) && row.key.startsWith(`${MYDAY_CACHE_PREFIX}v2|${row.scope}|`) && isMyDaySnapshotValid(row.data, row.scope!, now);
      if (!ok) await deleteCache(row.key);
    }
  } catch { /* IndexedDB nedostupan */ }
}

/** Odjava: ukloni sve predmemorirane podatke korisnika s uređaja („Moj dan” i lista obilazaka). */
export async function clearUserDataCaches(): Promise<void> {
  clearAllMyDaySnapshots();
  try { localStorage.removeItem('zoma_visits_page_v1'); } catch { /* nedostupno */ }
  try { await clearAllCache(); } catch { /* IndexedDB nedostupan */ }
}

export async function loadVisitsPageCacheFirst(scope?: string): Promise<{
  data: VisitsPageData | null;
  savedAt: string | null;
}> {
  const cached = await getCache<VisitsPageData & { savedAt?: string }>(scope ? `${CACHE_KEYS.visitsPage}:${scope}` : CACHE_KEYS.visitsPage);
  if (cached) {
    return { data: cached.data, savedAt: cached.savedAt };
  }
  if (scope) return { data: null, savedAt: null };
  const { loadVisitsPageCache } = await import('./visitsPageCache');
  const legacy = loadVisitsPageCache();
  if (legacy) {
    return {
      data: {
        visits: legacy.visits,
        locations: legacy.locations,
        visitReasons: legacy.visitReasons || [],
      },
      savedAt: legacy.savedAt,
    };
  }
  return { data: null, savedAt: null };
}

export async function fetchAndCacheVisitsPage(companyId?: string, scope?: string, isCurrent: () => boolean = () => true, filters: { start_date?: string; end_date?: string; status?: string } = {}): Promise<VisitsPageData & { savedAt: string }> {
  const params = companyId ? { company_id: companyId } : {};
  const loadVisits = async () => {
    const first = await api.get('/visits', { params: { ...params, ...filters, limit: 50 } });
    if (!Object.keys(filters).length) return first;
    const visits = [...(first.data.visits || [])];
    for (let page = 2; page <= (first.data.pagination?.pages || 1); page++) {
      if (!isCurrent()) return first;
      const next = await api.get('/visits', { params: { ...params, ...filters, limit: 50, page } });
      visits.push(...(next.data.visits || []));
    }
    return { data: { visits } };
  };
  const results = await Promise.allSettled([
    loadVisits(),
    api.get('/locations/map', { params }),
    api.get('/visit-reasons', { params }),
  ]);
  const keys = ['visits', 'locations', 'visitReasons'] as const;
  const data: VisitsPageData = { visits: [], locations: [], visitReasons: [], errors: {} };
  results.forEach((result, index) => {
    const key = keys[index];
    if (result.status === 'fulfilled') data[key] = result.value.data[key] || [];
    else data.errors![key] = result.reason?.response?.data?.message || 'Učitavanje nije uspjelo.';
  });
  const savedAt = new Date().toISOString();
  if (isCurrent() && !Object.keys(data.errors!).length) {
    try {
      await setCache(scope ? `${CACHE_KEYS.visitsPage}:${scope}` : CACHE_KEYS.visitsPage, { ...data, savedAt });
      if (!scope) { const { saveVisitsPageCache } = await import('./visitsPageCache'); saveVisitsPageCache(data); }
    } catch { /* A cache write must not turn a successful HTTP response into an error. */ }
  }
  return { ...data, savedAt };
}
